import {
  AGEING_BUCKETS,
  CLAIM_STATES,
  PAYMENT_METHODS,
  REPORT_PERIOD_LIMIT_DAYS,
  SERVICE_ITEM_CATEGORIES,
  type SummaryLink,
} from '@wodi/domain';
import { z } from 'zod';

import type { Permission } from '../permissions.ts';
import { isoDaySchema } from './clinical-common.ts';
import { amountSchema, signedAmountSchema } from './money.ts';

// Reports (docs/plans/phase1-reports.md §2, §3, §8). Every report is read through RLS in
// a read-only transaction. Filters travel in the request body, never in the URL, because
// a register filter can identify a patient (CLAUDE.md: no PHI in URLs). Money is cents in
// strings (ADR 0006); instants are ISO strings, shown in EAT by the client.

/** The reports that exist in Phase 1, by the key used in routes, audit and file names. */
export const REPORT_KEYS = [
  'daily-collections',
  'revenue',
  'payer-share-unclaimed',
  'day-summary',
  'opd-register',
  'lab-register',
  'pharmacy-register',
] as const;
export type ReportKey = (typeof REPORT_KEYS)[number];

/**
 * What each report needs to be read (printing too), and whether it lists patients.
 * Downloading also needs `reports.export`. Registers list patients: their views are
 * audited and labelled visits are left out for readers not granted the labels (R13).
 */
export const REPORT_ACCESS: Record<
  ReportKey,
  { readonly permissions: readonly Permission[]; readonly listsPatients: boolean }
> = {
  'daily-collections': { permissions: ['reports.finance.read'], listsPatients: false },
  revenue: { permissions: ['reports.finance.read'], listsPatients: false },
  'payer-share-unclaimed': { permissions: ['reports.claims.read'], listsPatients: false },
  'day-summary': { permissions: ['reports.summary.read'], listsPatients: false },
  'opd-register': { permissions: ['reports.registers.read'], listsPatients: true },
  'lab-register': { permissions: ['reports.registers.read', 'lab.read'], listsPatients: true },
  'pharmacy-register': {
    permissions: ['reports.registers.read', 'stock.read'],
    listsPatients: true,
  },
};

/** Where each link in the owner's summary sentence goes. */
export const SUMMARY_LINK_REPORT: Record<SummaryLink, ReportKey> = {
  opd_register: 'opd-register',
  revenue: 'revenue',
  collections: 'daily-collections',
  claims: 'payer-share-unclaimed',
};

export { REPORT_PERIOD_LIMIT_DAYS };

// --- Requests ----------------------------------------------------------------------------

export const reportPeriodSchema = z.object({ from: isoDaySchema, to: isoDaySchema });
export type ReportPeriod = z.infer<typeof reportPeriodSchema>;

/** Omitted: the facility the reader is working at. Several: each needs the permission. */
const facilityIdsSchema = z.array(z.uuid()).min(1).max(50).optional();

export const moneyReportQuerySchema = z.object({
  period: reportPeriodSchema,
  facilityIds: facilityIdsSchema,
});
export type MoneyReportQuery = z.infer<typeof moneyReportQuerySchema>;

export const unclaimedQuerySchema = z.object({ facilityIds: facilityIdsSchema });

export const daySummaryQuerySchema = z.object({
  day: isoDaySchema,
  facilityIds: facilityIdsSchema,
});
export type DaySummaryQuery = z.infer<typeof daySummaryQuerySchema>;

const registerPage = {
  cursor: z.uuid().optional(),
  limit: z.number().int().min(1).max(200).default(100),
};

export const opdRegisterQuerySchema = z.object({
  period: reportPeriodSchema,
  servicePointId: z.uuid().optional(),
  payerId: z.uuid().optional(),
  visitType: z.enum(['outpatient', 'walk_in']).optional(),
  /** Visits with a current diagnosis of exactly this code. */
  diagnosisCode: z.string().trim().min(1).max(40).optional(),
  ...registerPage,
});
export type OpdRegisterQuery = z.infer<typeof opdRegisterQuerySchema>;

export const labRegisterQuerySchema = z.object({
  period: reportPeriodSchema,
  sectionId: z.uuid().optional(),
  ...registerPage,
});
export type LabRegisterQuery = z.infer<typeof labRegisterQuerySchema>;

/** A dispense line given against a prescription, or a counter sale line. */
export const PHARMACY_REGISTER_KINDS = ['dispense', 'sale'] as const;
export type PharmacyRegisterKind = (typeof PHARMACY_REGISTER_KINDS)[number];

export const pharmacyRegisterQuerySchema = z.object({
  period: reportPeriodSchema,
  kind: z.enum(PHARMACY_REGISTER_KINDS).optional(),
  storeId: z.uuid().optional(),
  ...registerPage,
});
export type PharmacyRegisterQuery = z.infer<typeof pharmacyRegisterQuerySchema>;

/** Recorded before the browser's print dialog opens (plan §8). */
export const reportPrintedBodySchema = z.object({
  period: reportPeriodSchema,
  facilityIds: z.array(z.uuid()).min(1).max(50),
  rowCount: z.number().int().min(0),
});

// --- Shared pieces -----------------------------------------------------------------------

const facilityRefSchema = z.object({
  facilityId: z.uuid(),
  name: z.string(),
  /** The short code used in numbers and file names (F1). */
  code: z.string().nullable(),
  mflCode: z.string().nullable(),
});

const reportStampSchema = {
  /** When the data was read (the snapshot of the read-only transaction). */
  dataAsAt: z.iso.datetime(),
};

export const reportFacilitiesSchema = z.object({
  facilities: z.array(facilityRefSchema.extend({ reports: z.array(z.enum(REPORT_KEYS)) })),
});
export type ReportFacilitiesView = z.infer<typeof reportFacilitiesSchema>;

const methodTotalsSchema = z.object({
  received: amountSchema,
  refunded: amountSchema,
  net: signedAmountSchema,
});

const byMethodSchema = z.array(methodTotalsSchema.extend({ method: z.enum(PAYMENT_METHODS) }));

// --- Daily collections -------------------------------------------------------------------

export const collectionsFacilitySchema = facilityRefSchema.extend({
  byMethod: byMethodSchema,
  total: methodTotalsSchema,
  paymentCount: z.number().int(),
  shifts: z.array(
    z.object({
      shiftId: z.uuid(),
      number: z.string(),
      cashierName: z.string(),
      status: z.enum(['open', 'closed']),
      openedAt: z.iso.datetime(),
      closedAt: z.iso.datetime().nullable(),
      /** Completed payments in the period on this shift. */
      receivedInPeriod: amountSchema,
      paymentCount: z.number().int(),
      variance: signedAmountSchema.nullable(),
      varianceReason: z.string().nullable(),
      reconciliation: z.enum(['open', 'reconciles', 'differs']),
      differingMethods: z.array(z.enum(PAYMENT_METHODS)),
    }),
  ),
  /** M-Pesa, card and bank references for reconciliation, oldest first. */
  references: z.array(
    z.object({
      receiptNumber: z.string().nullable(),
      method: z.enum(PAYMENT_METHODS),
      reference: z.string().nullable(),
      amount: amountSchema,
      receivedAt: z.iso.datetime(),
      /** Null for a paybill payment, which no cashier's shift holds (ADR 0020). */
      shiftNumber: z.string().nullable(),
    }),
  ),
  referencesTruncated: z.boolean(),
  /** Payments received in the period but entered on a later day (P16). */
  lateEntries: z.array(
    z.object({
      receiptNumber: z.string().nullable(),
      method: z.enum(PAYMENT_METHODS),
      amount: amountSchema,
      receivedAt: z.iso.datetime(),
      enteredAt: z.iso.datetime(),
    }),
  ),
});

export const collectionsReportSchema = z.object({
  period: reportPeriodSchema,
  ...reportStampSchema,
  facilities: z.array(collectionsFacilitySchema),
  /** Across facilities, when there is more than one. */
  total: z.object({ byMethod: byMethodSchema, total: methodTotalsSchema }).nullable(),
});
export type CollectionsReportView = z.infer<typeof collectionsReportSchema>;

// --- Revenue -------------------------------------------------------------------------------

const revenueLineSchema = z.object({
  chargeCount: z.number().int(),
  unpricedCount: z.number().int(),
  billed: amountSchema,
  payerShare: amountSchema,
  patientShare: amountSchema,
});

export const revenueFacilitySchema = facilityRefSchema.extend({
  byCategory: z.array(revenueLineSchema.extend({ category: z.enum(SERVICE_ITEM_CATEGORIES) })),
  byPayer: z.array(
    revenueLineSchema.extend({
      payerId: z.uuid(),
      payerName: z.string(),
      schemeId: z.uuid().nullable(),
      schemeName: z.string().nullable(),
    }),
  ),
  billed: amountSchema,
  waivers: amountSchema,
  discounts: amountSchema,
  creditNotes: amountSchema,
  net: signedAmountSchema,
  collected: signedAmountSchema,
  unpricedCount: z.number().int(),
});

export const revenueReportSchema = z.object({
  period: reportPeriodSchema,
  ...reportStampSchema,
  facilities: z.array(revenueFacilitySchema),
});
export type RevenueReportView = z.infer<typeof revenueReportSchema>;

// --- Payer share not yet claimed (P14) ---------------------------------------------------------

export const unclaimedReportSchema = z.object({
  asAt: isoDaySchema,
  ...reportStampSchema,
  facilities: z.array(
    facilityRefSchema.extend({
      rows: z.array(
        z.object({
          payerId: z.uuid(),
          payerName: z.string(),
          chargeCount: z.number().int(),
          byBucket: z.object(
            Object.fromEntries(AGEING_BUCKETS.map((b) => [b, amountSchema])) as Record<
              (typeof AGEING_BUCKETS)[number],
              typeof amountSchema
            >,
          ),
          total: amountSchema,
        }),
      ),
      total: amountSchema,
      /**
       * The SHA claims pipeline (plan §2.7): claims by their state in Wodi, with what was
       * claimed and what SHA approved and paid. Claims entered in error are left out.
       */
      claims: z.array(
        z.object({
          state: z.enum(CLAIM_STATES),
          label: z.string(),
          claimCount: z.number().int(),
          claimed: amountSchema,
          approved: amountSchema,
          paid: amountSchema,
        }),
      ),
    }),
  ),
});
export type UnclaimedReportView = z.infer<typeof unclaimedReportSchema>;

// --- Owner's day summary -------------------------------------------------------------------------

const summaryFiguresSchema = z.object({
  patients: z.number().int(),
  visits: z.number().int(),
  billed: amountSchema,
  collected: signedAmountSchema,
  payerShareUnclaimed: amountSchema,
  unpricedCount: z.number().int(),
});

export const daySummaryFacilitySchema = facilityRefSchema.extend({
  figures: summaryFiguresSchema,
  byCategory: z.array(
    z.object({ category: z.enum(SERVICE_ITEM_CATEGORIES), billed: amountSchema }),
  ),
  byPayer: z.array(
    z.object({
      payerId: z.uuid(),
      payerName: z.string(),
      billed: amountSchema,
      payerShare: amountSchema,
    }),
  ),
  waiting: z.object({
    count: z.number().int(),
    medianMinutes: z.number().int().nullable(),
    p90Minutes: z.number().int().nullable(),
  }),
  /** Null for readers without stock.read. */
  stock: z
    .object({
      expiredBatches: z.number().int(),
      nearExpiryBatches: z.number().int(),
      itemsAtOrBelowReorder: z.number().int(),
    })
    .nullable(),
});

export const daySummarySchema = z.object({
  day: isoDaySchema,
  ...reportStampSchema,
  facilities: z.array(daySummaryFacilitySchema),
  total: summaryFiguresSchema.nullable(),
});
export type DaySummaryView = z.infer<typeof daySummarySchema>;

// --- Registers ---------------------------------------------------------------------------------

export const opdRegisterRowSchema = z.object({
  visitId: z.uuid(),
  startedAt: z.iso.datetime(),
  visitNumber: z.string(),
  patientNumber: z.string(),
  patientName: z.string(),
  sex: z.string(),
  /** `34 years`, `18 months`; null when the birth date isn't recorded. */
  age: z.string().nullable(),
  ageEstimated: z.boolean(),
  residence: z.string().nullable(),
  payers: z.array(z.string()),
  servicePoints: z.array(z.string()),
  type: z.enum(['outpatient', 'walk_in']),
  status: z.enum(['open', 'closed', 'cancelled']),
  /**
   * Current provisional and confirmed diagnoses, primary first (plan §10.1). `code` is null
   * for a diagnosis recorded in words only. Null when diagnoses can't be read.
   */
  diagnoses: z
    .array(
      z.object({
        code: z.string().nullable(),
        display: z.string(),
        certainty: z.enum(['provisional', 'confirmed']),
      }),
    )
    .nullable(),
});
export type OpdRegisterRow = z.infer<typeof opdRegisterRowSchema>;

/** `T-0001 Title`, or the clinician's words when uncoded; "(provisional)" when it is. */
export function diagnosisWords(d: NonNullable<OpdRegisterRow['diagnoses']>[number]): string {
  const text = d.code === null ? d.display : `${d.code} ${d.display}`;
  return d.certainty === 'provisional' ? `${text} (provisional)` : text;
}

export const opdRegisterSchema = z.object({
  period: reportPeriodSchema,
  ...reportStampSchema,
  facility: facilityRefSchema,
  diagnosesAvailable: z.boolean(),
  rows: z.array(opdRegisterRowSchema),
  nextCursor: z.uuid().nullable(),
});
export type OpdRegisterView = z.infer<typeof opdRegisterSchema>;

export const labRegisterRowSchema = z.object({
  orderId: z.uuid(),
  orderedAt: z.iso.datetime(),
  sampleNumbers: z.array(z.string()),
  collectedAt: z.iso.datetime().nullable(),
  patientNumber: z.string(),
  patientName: z.string(),
  testName: z.string(),
  sectionName: z.string(),
  priority: z.string(),
  orderStatus: z.enum(['active', 'completed', 'cancelled', 'entered_in_error']),
  reportStatus: z.string().nullable(),
  releasedAt: z.iso.datetime().nullable(),
  /** Collected to released, in whole minutes. */
  turnaroundMinutes: z.number().int().nullable(),
  /** Flags only: result values stay in the patient's record (P12). */
  abnormalCount: z.number().int(),
  critical: z.boolean(),
});
export type LabRegisterRow = z.infer<typeof labRegisterRowSchema>;

export const labRegisterSchema = z.object({
  period: reportPeriodSchema,
  ...reportStampSchema,
  facility: facilityRefSchema,
  rows: z.array(labRegisterRowSchema),
  nextCursor: z.uuid().nullable(),
});
export type LabRegisterView = z.infer<typeof labRegisterSchema>;

export const pharmacyRegisterRowSchema = z.object({
  lineId: z.uuid(),
  kind: z.enum(PHARMACY_REGISTER_KINDS),
  /** When the medicine was handed over: dispensed, or the sale completed. */
  at: z.iso.datetime(),
  /** The dispense or sale number. */
  number: z.string(),
  storeName: z.string(),
  /** Null for a counter sale to a buyer who wasn't identified. */
  patientNumber: z.string().nullable(),
  patientName: z.string().nullable(),
  itemCode: z.string(),
  itemName: z.string(),
  quantity: z.number().int(),
  unit: z.string(),
  controlled: z.boolean(),
  batchNumbers: z.array(z.string()),
  /** The prescriber as captured; for a sale, the outside prescriber if one was recorded. */
  prescriber: z.string().nullable(),
  prescribedOutside: z.boolean(),
  handedOverBy: z.string(),
  /** Brought back and accepted on an approved return. */
  returnedQuantity: z.number().int(),
});
export type PharmacyRegisterRow = z.infer<typeof pharmacyRegisterRowSchema>;

export const pharmacyRegisterSchema = z.object({
  period: reportPeriodSchema,
  ...reportStampSchema,
  facility: facilityRefSchema,
  rows: z.array(pharmacyRegisterRowSchema),
  nextCursor: z.uuid().nullable(),
});
export type PharmacyRegisterView = z.infer<typeof pharmacyRegisterSchema>;

export type ReportFacilityRef = z.infer<typeof facilityRefSchema>;
